<template>
    <v-container fluid>
      <!-- —— header —— -->
      <v-card elevation="2">
        <v-card-title class="d-flex align-center tableheader white--text">
          <v-icon left color="white">mdi-help-circle</v-icon>
          Help Center
          <v-spacer/>
          <!-- search -->
          <v-text-field
            v-model="search"
            label="Search help topics"
            prepend-inner-icon="mdi-magnify"
            density="compact"
            variant="solo-filled"
            flat hide-details single-line
            class="me-4"
          />
          <!-- expand / collapse -->
          <v-btn color="primary" class="me-2" @click="expandAll" :title="'Expand all topics'">
            <v-icon>mdi-arrow-expand-all</v-icon>
          </v-btn>
          <v-btn color="primary" class="me-2" @click="collapseAll" :title="'Collapse all topics'">
            <v-icon>mdi-arrow-collapse-all</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider/>
        <!-- —— help topics —— -->
        <v-card-text>
          <v-expansion-panels v-model="activePanels" multiple>
            <v-expansion-panel
              v-for="(topic, idx) in filteredTopics"
              :key="idx"
            >
              <v-expansion-panel-title class="text-subtitle-1 font-weight-medium">
                <v-icon left class="mr-2">{{ topic.icon }}</v-icon>
                {{ topic.title }}
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <div v-html="topic.content"></div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
        <!-- —— footer —— -->
        <v-card-actions class="bg-grey-lighten-4 pa-3">
          <span class="text-caption">Can't find what you're looking for? Contact support at <a href="mailto:admin@bagusphere.com">admin@bagusphere.com</a>.</span>
          <v-spacer></v-spacer>
          <v-btn icon="mdi-arrow-up" variant="text" @click="scrollToTop"/>
        </v-card-actions>
      </v-card>
    </v-container>
  </template>
  
  <script setup>
  import { ref, computed, onMounted } from 'vue';
  
  /* —— state —— */
  const search = ref('');
  const activePanels = ref([]);
  
  /* —— help topics data —— */
  const topics = [
    {
      title: 'Getting Started',
      icon: 'mdi-rocket-launch-outline',
      content: `
        <h3 class="font-weight-medium">Welcome Aboard</h3>
        <p>Follow these steps to hit the ground running:</p>
        <ol>
          <li><strong>Create Units</strong> - Navigate to <em>Units &gt; Add Unit</em> and enter basic details.</li>
          <li><strong>Add Tenants</strong> - Go to <em>Tenants &gt; Add Tenant</em> and assign each tenant to a unit.</li>
          <li><strong>Record Opening Balances</strong> - Under <em>Payments</em>, log any deposits or prorated rent.</li>
        </ol>
        <v-alert type="info" density="compact" class="my-3">
          <v-icon size="16" left>mdi-lightbulb-on-outline</v-icon>
          Tip: Use the sample data generator (Profile &gt; Settings) if you just want to explore features.
        </v-alert>
      `
    },
    {
      title: 'Dashboard / Home',
      icon: 'mdi-view-dashboard',
      content: `
        <h3 class="font-weight-medium">Overview</h3>
        <p>The <strong>Home</strong> dashboard gives you a bird's-eye view of key metrics such as <em>Total Units</em>, <em>Occupancy Rate</em>, <em>Rent Due This Week</em> and <em>Open Maintenance Tickets</em>.</p>
        <ul>
          <li>Click any metric card to jump straight to the detailed page.</li>
          <li>Hover over charts to see exact values and timelines.</li>
          <li>The dashboard auto-refreshes every 10 minutes or whenever you navigate back to it.</li>
        </ul>
        <v-alert type="success" density="compact" class="my-3">
          Use the <strong>Export Snapshot</strong> button (top-right) to download a PDF report of today's metrics.
        </v-alert>
      `
    },
    {
      title: 'Units',
      icon: 'mdi-home-city',
      content: `
        <h3 class="font-weight-medium">Managing Units</h3>
        <p>The <strong>Units</strong> page lists every property unit you manage.</p>
        <ol>
          <li>Use the <em>Search (name, address)</em> box to quickly filter units.</li>
          <li>Select <em>Add Unit</em> to create a new record. Required fields are marked with <span class="text-red">*</span>.</li>
          <li>Click the eye<v-icon size="16">mdi-eye-outline</v-icon> icon to view full details, the pencil <v-icon size="16">mdi-pencil</v-icon> to edit (admins only) or the trash <v-icon size="16" color="error">mdi-delete</v-icon> to delete.</li>
          <li>The <em>Status</em> chip shows <strong class="text-green">Occupied</strong>, <strong class="text-orange">Vacant</strong> or <strong class="text-blue">Maintenance</strong>.</li>
        </ol>
        <h4 class="mt-4 font-weight-medium">Bulk Actions</h4>
        <ul>
          <li>Select multiple units using the check-boxes, then choose <em>Bulk → Export CSV</em> or <em>Bulk → Archive</em>.</li>
        </ul>
      `
    },
    {
      title: 'Tenants',
      icon: 'mdi-account-group',
      content: `
        <h3 class="font-weight-medium">Tenant Records</h3>
        <p>The <strong>Tenants</strong> module links occupants to their respective units and rental agreements.</p>
        <ul>
          <li><strong>Add Tenant</strong> opens a form to capture personal details and assign a unit.</li>
          <li>Editing a tenant lets you extend leases, adjust rent or end occupancy with a move-out date.</li>
          <li>Deleting a tenant automatically frees the unit and archives payment history.</li>
        </ul>
        <h4 class="mt-4 font-weight-medium">Document Vault</h4>
        <p>Upload ID scans, signed contracts or inspection photos under the <em>Attachments</em> tab inside each tenant's profile.</p>
      `
    },
    {
      title: 'Payments',
      icon: 'mdi-cash-register',
      content: `
        <h3 class="font-weight-medium">Recording Rent & Fees</h3>
        <p>Track all incoming payments under <strong>Payments</strong>.</p>
        <ol>
          <li><em>Add Payment</em> to capture rent, deposits or other charges.</li>
          <li>Fields auto-populate when you select a tenant—including amount due and outstanding balance.</li>
          <li>The <em>Balance</em> column turns <span class="text-red">red</span> if overdue; hover to see days past due.</li>
        </ol>
      `
    },
    {
      title: 'Expenses',
      icon: 'mdi-cash-multiple',
      content: `
        <h3 class="font-weight-medium">Operating Expenses</h3>
        <p>Log maintenance costs, utility bills and other outgoings.</p>
        <ul>
          <li>The list supports real-time search by <em>Category</em> or <em>Unit</em>.</li>
          <li>Click <strong>Add Expense</strong> to create a new entry; amount accepts decimals.</li>
          <li>Select a <em>Vendor</em> or leave blank if not applicable.</li>
          <li>Admins can edit or delete any expense; other users may view only.</li>
        </ul>
        <h4 class="mt-4 font-weight-medium">Recurring Expenses</h4>
        <p>Set utility bills (e.g., Internet, Water) to recur monthly so you never forget to log them.</p>
      `
    },
    {
      title: 'Vendors',
      icon: 'mdi-truck-delivery-outline',
      content: `
        <h3 class="font-weight-medium">Service Providers</h3>
        <p>Store contact information for contractors, utility companies and suppliers.</p>
        <ul>
          <li>Vendor names populate the dropdown on the <strong>Expenses</strong> form.</li>
          <li>You can mark a vendor as <em>Inactive</em> to hide them from new expense entries while preserving history.</li>
          <li>Add rating stars to track reliability and cost-effectiveness.</li>
        </ul>
      `
    },
    {
      title: 'Maintenance Requests',
      icon: 'mdi-wrench-clock',
      content: `
        <h3 class="font-weight-medium">Issue Tracking</h3>
        <p>Submit and monitor maintenance tickets:</p>
        <ol>
          <li><strong>New Request</strong> requires a unit, description and priority.</li>
          <li>Status progresses from <em>Open → In Progress → Completed</em>.</li>
          <li>Link a vendor and convert costs directly into an <strong>Expense</strong>.</li>
        </ol>
        <v-alert type="warning" density="compact" class="my-3">
          Emergency requests (priority = "Critical") automatically notify the assigned manager via SMS.
        </v-alert>
      `
    },
    {
      title: 'Occupancy',
      icon: 'mdi-chart-bell-curve-cumulative',
      content: `
        <h3 class="font-weight-medium">Occupancy Analytics</h3>
        <p>Visualize vacancy rates and rental income forecasts.</p>
        <ul>
          <li>Filter by property or date range to update charts.</li>
          <li>The table beneath lists upcoming lease expirations.</li>
          <li>Download the graph as PNG or include it in the monthly PDF report.</li>
        </ul>
      `
    },
    // {
    //   title: 'Reports & Exports',
    //   icon: 'mdi-file-chart',
    //   content: `
    //     <h3 class="font-weight-medium">Financial & Operational Reports</h3>
    //     <p>Generate statement of cash flow, P&L, occupancy trend and more.</p>
    //     <ul>
    //       <li>Select the report, date range and output format (PDF, CSV or XLSX).</li>
    //       <li>Reports respect user permissions—viewers only see data for units they can access.</li>
    //     </ul>
    //   `
    // },
    {
      title: 'User Roles & Permissions',
      icon: 'mdi-shield-account',
      content: `
        <h3 class="font-weight-medium">Access Control</h3>
        <p>Administrators can assign roles such as <em>Admin</em>, <em>Manager</em> or <em>Viewer</em>.</p>
        <ul>
          <li>Only Admins can delete records and manage roles.</li>
          <li>Changes take effect immediately on the next page load.</li>
          <li>Use custom roles to fine-tune permissions (<em>Administration &gt; Roles</em>).</li>
        </ul>
      `
    },
    {
      title: 'Account & Authentication',
      icon: 'mdi-account-key',
      content: `
        <h3 class="font-weight-medium">Login & Registration</h3>
        <p>New users may register via the <strong>Register</strong> page (admins can disable self-registration).</p>
        <ul>
          <li>Password must contain at least 8 characters, including a number.</li>
          <li>After 5 failed attempts the account is locked for 15 minutes.</li>
          <li>Reset your password via <em>Forgot Password</em>—a one-time link expires in 30 minutes.</li>
        </ul>
      `
    },
    {
      title: 'FAQ',
      icon: 'mdi-comment-question-outline',
      content: `
        <h3 class="font-weight-medium">Frequently Asked Questions</h3>
        <p><strong>Q:</strong> Can I import data from my old system?<br>
        <strong>A:</strong> Yes. Go to <em>Settings &gt; Import</em> to upload CSV files for units, tenants and payments.</p>
        <p><strong>Q:</strong> How do I enable 2-factor authentication?<br>
        <strong>A:</strong> Open <em>Profile &gt; Security</em> and follow the on-screen instructions.</p>
        <p><strong>Q:</strong> Where are my files stored?<br>
        <strong>A:</strong> All documents are encrypted and stored in an EU data centre compliant with GDPR.</p>
      `
    },
    {
      title: 'Glossary',
      icon: 'mdi-book-open-variant',
      content: `
        <h3 class="font-weight-medium">Key Terms</h3>
        <ul>
          <li><strong>Unit:</strong> A rentable entity—apartment, house, office space, etc.</li>
          <li><strong>Tenant:</strong> Occupant who has signed a lease agreement.</li>
          <li><strong>Lease:</strong> Legal contract binding the tenant to a unit for a period.</li>
          <li><strong>Expense:</strong> Money paid out for operations (maintenance, utilities).</li>
          <li><strong>Vendor:</strong> Service-provider or supplier linked to expenses.</li>
        </ul>
      `
    }
  ];
  
  /* —— computed —— */
  const filteredTopics = computed(() => {
    const q = search.value.toLowerCase();
    if (!q) return topics;
    return topics.filter(t =>
      t.title.toLowerCase().includes(q) || t.content.toLowerCase().includes(q)
    );
  });
  
  /* —— helpers —— */
  function scrollToTop() {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }
  
  function expandAll() {
    activePanels.value = filteredTopics.value.map((_, idx) => idx);
  }
  
  function collapseAll() {
    activePanels.value = [];
  }
  
  /* —— lifecycle —— */
  onMounted(() => {
    // Open Getting Started by default
    activePanels.value = [0];
  });
  </script>
  
  <style scoped>
  .tableheader {
    background: #ffffff; /* keep consistent with app primary */
  }
  </style>
  